import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import axios from 'axios';
import QRCode from 'qrcode';
import './FundTransferKeypad.css';

const FALLBACK_TWIN_PARKS = [
  { name: "Twin Park - Tunisia Mall", category: "Twin Park" },
  { name: "Twin Park- Manar City", category: "Twin Park" },
  { name: "Twin park - Jardins d'eL Menzah", category: "Twin Park" },
  { name: "Jump Zone Mall of Djerba", category: "Twin Park" }
];

const GestionAgentsTransfert = () => {
  const reduxUser = useSelector((state) => state.AuthReducer?.user);
  const currentUser = reduxUser || JSON.parse(localStorage.getItem('user') || 'null') || { name: 'Admin', role: 'admin' };

  // Data states
  const [agents, setAgents] = useState([]);
  const [locations, setLocations] = useState(FALLBACK_TWIN_PARKS);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);

  // Form states
  const [isEditing, setIsEditing] = useState(false);
  const [editingAgentId, setEditingAgentId] = useState(null);
  const [name, setName] = useState('');
  const [pinCode, setPinCode] = useState('');
  const [localisation, setLocalisation] = useState('Twin Park - Tunisia Mall');
  const [role, setRole] = useState('caissier');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Filter & Search
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLocFilter, setSelectedLocFilter] = useState('ALL');
  const [showPinMap, setShowPinMap] = useState({});

  // Quick PIN Change Modal
  const [pinModalOpen, setPinModalOpen] = useState(false);
  const [targetAgentForPin, setTargetAgentForPin] = useState(null);
  const [newPinInput, setNewPinInput] = useState('');

  // Mobile QR Onboarding Modal
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedAgentForQr, setSelectedAgentForQr] = useState(null);
  const [agentQrDataUrl, setAgentQrDataUrl] = useState('');

  // PIN Tester
  const [testPin, setTestPin] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testingPin, setTestingPin] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const getHeaders = () => {
    const token = localStorage.getItem('token') || localStorage.getItem('ft_token');
    return token ? { Authorization: `Bearer ${token}`, Authorised: token } : {};
  };

  const showToast = (msg, type = 'success') => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const headers = getHeaders();

      // 1. Fetch Agents
      try {
        const agentsRes = await axios.get('/api/fund-transfers/agents', { headers });
        if (agentsRes.data?.success) {
          setAgents(agentsRes.data.agents || []);
        }
      } catch (e) {
        console.warn('Erreur chargement agents:', e.message);
      }

      // 2. Fetch Locations
      try {
        const locsRes = await axios.get('/api/fund-transfers/twin-parks', { headers });
        if (locsRes.data?.locations && locsRes.data.locations.length > 0) {
          setLocations(locsRes.data.locations);
          if (!localisation) setLocalisation(locsRes.data.locations[0].name);
        } else {
          // Deuxième tentative via la route globale des emplacements
          const allLocs = await axios.get('/api/Locations/getAllLocations', { headers }).catch(() => null);
          if (allLocs?.data && Array.isArray(allLocs.data) && allLocs.data.length > 0) {
            const twinOnly = allLocs.data.filter(l => l.category === 'Twin Park' || (l.name && l.name.toLowerCase().includes('twin')));
            setLocations(twinOnly.length > 0 ? twinOnly : allLocs.data);
          } else {
            setLocations(FALLBACK_TWIN_PARKS);
          }
        }
      } catch (locErr) {
        console.warn('Utilisation des parcs par défaut:', locErr.message);
        setLocations(FALLBACK_TWIN_PARKS);
      }
    } catch (err) {
      console.error('Erreur chargement données:', err);
    } finally {
      setLoading(false);
    }
  };


  const handleKeypadPress = (val, target = 'form') => {
    if (target === 'form') {
      if (pinCode.length < 4) {
        setPinCode(prev => prev + val);
      }
    } else if (target === 'modal') {
      if (newPinInput.length < 4) {
        setNewPinInput(prev => prev + val);
      }
    } else if (target === 'tester') {
      if (testPin.length < 4) {
        const updated = testPin + val;
        setTestPin(updated);
        if (updated.length === 4) {
          runPinTest(updated);
        }
      }
    }
  };

  const handleKeypadDelete = (target = 'form') => {
    if (target === 'form') {
      setPinCode(prev => prev.slice(0, -1));
    } else if (target === 'modal') {
      setNewPinInput(prev => prev.slice(0, -1));
    } else if (target === 'tester') {
      setTestPin(prev => prev.slice(0, -1));
      setTestResult(null);
    }
  };

  const handleKeypadClear = (target = 'form') => {
    if (target === 'form') {
      setPinCode('');
    } else if (target === 'modal') {
      setNewPinInput('');
    } else if (target === 'tester') {
      setTestPin('');
      setTestResult(null);
    }
  };

  const resetForm = () => {
    setIsEditing(false);
    setEditingAgentId(null);
    setName('');
    setPinCode('');
    setRole('caissier');
    setPhone('');
    setNotes('');
    if (locations.length > 0) {
      setLocalisation(locations[0].name);
    }
  };

  const handleEditClick = (agent) => {
    setIsEditing(true);
    setEditingAgentId(agent._id);
    setName(agent.name || '');
    setPinCode(agent.pinCode || '');
    setLocalisation(agent.localisation || '');
    setRole(agent.role || 'caissier');
    setPhone(agent.phone || '');
    setNotes(agent.notes || '');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!name.trim()) {
      showToast('Le nom de l\'agent est obligatoire.', 'error');
      return;
    }
    if (!pinCode || pinCode.length !== 4 || !/^\d{4}$/.test(pinCode)) {
      showToast('Le code PIN doit comporter exactement 4 chiffres numériques.', 'error');
      return;
    }
    if (!localisation.trim()) {
      showToast('La localisation est obligatoire.', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const payload = {
        name: name.trim(),
        pinCode: pinCode.trim(),
        localisation: localisation.trim(),
        role,
        phone: phone.trim(),
        notes: notes.trim()
      };

      if (isEditing && editingAgentId) {
        const res = await axios.put(`/api/fund-transfers/agents/${editingAgentId}`, payload, { headers });
        if (res.data?.success) {
          showToast(`Agent "${name}" mis à jour avec succès.`);
          resetForm();
          fetchInitialData();
        }
      } else {
        const res = await axios.post('/api/fund-transfers/agents', payload, { headers });
        if (res.data?.success) {
          showToast(`Agent "${name}" créé avec succès.`);
          resetForm();
          fetchInitialData();
        }
      }
    } catch (err) {
      console.error('Erreur enregistrement agent:', err);
      const errMsg = err.response?.data?.msg || 'Erreur lors de l\'enregistrement de l\'agent.';
      showToast(errMsg, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (agent) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'agent "${agent.name}" (${agent.localisation}) ?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const res = await axios.delete(`/api/fund-transfers/agents/${agent._id}`, { headers });
      if (res.data?.success) {
        showToast(`Agent "${agent.name}" supprimé.`);
        fetchInitialData();
      }
    } catch (err) {
      console.error('Erreur suppression agent:', err);
      showToast('Erreur lors de la suppression.', 'error');
    }
  };

  const handleToggleActive = async (agent) => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const res = await axios.put(`/api/fund-transfers/agents/${agent._id}`, {
        isActive: !agent.isActive
      }, { headers });

      if (res.data?.success) {
        showToast(`Statut de "${agent.name}" actualisé.`);
        fetchInitialData();
      }
    } catch (err) {
      console.error('Erreur modification statut:', err);
      showToast('Erreur modification statut.', 'error');
    }
  };

  const openPinModal = (agent) => {
    setTargetAgentForPin(agent);
    setNewPinInput('');
    setPinModalOpen(true);
  };

  const handleSaveQuickPin = async () => {
    if (!newPinInput || newPinInput.length !== 4 || !/^\d{4}$/.test(newPinInput)) {
      showToast('Le nouveau code PIN doit comporter 4 chiffres.', 'error');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const res = await axios.put(`/api/fund-transfers/agents/${targetAgentForPin._id}`, {
        pinCode: newPinInput.trim()
      }, { headers });

      if (res.data?.success) {
        showToast(`Code PIN de "${targetAgentForPin.name}" mis à jour avec succès.`);
        setPinModalOpen(false);
        setTargetAgentForPin(null);
        setNewPinInput('');
        fetchInitialData();
      }
    } catch (err) {
      console.error('Erreur mise à jour PIN:', err);
      showToast(err.response?.data?.msg || 'Erreur mise à jour PIN.', 'error');
    }
  };

  const openQrModal = async (agent) => {
    setSelectedAgentForQr(agent);
    try {
      const origin = window.location.origin;
      const mobilePortalUrl = `${origin}/transfert-mobile`;
      const qrUrl = await QRCode.toDataURL(mobilePortalUrl, {
        width: 320,
        margin: 2,
        color: { dark: '#000000', light: '#ffffff' }
      });
      setAgentQrDataUrl(qrUrl);
      setQrModalOpen(true);
    } catch (e) {
      console.error('Erreur génération QR agent:', e);
    }
  };

  const runPinTest = async (pin) => {
    setTestingPin(true);
    setTestResult(null);
    try {
      const res = await axios.post('/api/fund-transfers/public-pin-auth', { pinCode: pin });
      if (res.data?.success) {
        setTestResult({
          success: true,
          user: res.data.user,
          msg: `Authentification réussie ! Identifié comme "${res.data.user.name}" (${res.data.user.localisation})`
        });
      }
    } catch (err) {
      setTestResult({
        success: false,
        msg: err.response?.data?.msg || 'Code PIN invalide ou non autorisé'
      });
    } finally {
      setTestingPin(false);
    }
  };

  const toggleShowPin = (id) => {
    setShowPinMap(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const filteredAgents = agents.filter(a => {
    const matchesSearch = (a.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (a.localisation || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (a.pinCode || '').includes(searchTerm);
    const matchesLoc = selectedLocFilter === 'ALL' || a.localisation === selectedLocFilter;
    return matchesSearch && matchesLoc;
  });

  return (
    <div className="ft-portal-container">
      {/* Toast Notification */}
      {notification && (
        <div style={{
          position: 'fixed',
          top: 20,
          right: 20,
          zIndex: 9999,
          background: notification.type === 'error' ? '#ef4444' : '#10b981',
          color: '#ffffff',
          padding: '14px 22px',
          borderRadius: '12px',
          boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <span>{notification.type === 'error' ? '⚠️' : '✅'}</span>
          <span>{notification.msg}</span>
        </div>
      )}

      {/* Header Card */}
      <div className="ft-header-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 className="ft-header-title">⚙️ Configuration des Agents de Transfert & PIN</h1>
            <p className="ft-header-sub">
              Créez les identifiants et les codes PIN à 4 chiffres pour chaque responsable et caissier selon leur localisation.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Link to="/TransfertsFonds" className="ft-action-btn ft-btn-secondary" style={{ textDecoration: 'none', padding: '10px 16px' }}>
              💰 Module Transferts
            </Link>
            <Link to="/transfert-mobile" target="_blank" className="ft-action-btn ft-btn-primary" style={{ textDecoration: 'none', padding: '10px 16px' }}>
              📱 Tester Clavier Mobile
            </Link>
          </div>
        </div>
      </div>

      {/* Metrics Row */}
      <div className="ft-metrics-grid" style={{ marginBottom: 24 }}>
        <div className="ft-metric-box">
          <div className="ft-metric-label">Total Agents Configurés</div>
          <div className="ft-metric-value" style={{ color: '#38bdf8' }}>{agents.length}</div>
        </div>
        <div className="ft-metric-box">
          <div className="ft-metric-label">Agents Actifs</div>
          <div className="ft-metric-value" style={{ color: '#10b981' }}>{agents.filter(a => a.isActive).length}</div>
        </div>
        <div className="ft-metric-box">
          <div className="ft-metric-label">Parcs Couverts</div>
          <div className="ft-metric-value" style={{ color: '#f59e0b' }}>
            {new Set(agents.map(a => a.localisation)).size}
          </div>
        </div>
        <div className="ft-metric-box">
          <div className="ft-metric-label">Rôle Administrateur</div>
          <div className="ft-metric-value" style={{ color: '#ec4899' }}>
            {agents.filter(a => a.role === 'admin').length}
          </div>
        </div>
      </div>

      {/* Main Grid: Form / Keypad on Left, List & PIN Tester on Right */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 24 }}>
        
        {/* LEFT COLUMN: Agent Form & Virtual Keypad */}
        <div className="ft-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: '#f8fafc', margin: 0 }}>
              {isEditing ? '✏️ Modifier l\'Agent' : '➕ Créer un Nouvel Agent de Transfert'}
            </h2>
            {isEditing && (
              <button
                type="button"
                onClick={resetForm}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: 'none',
                  color: '#94a3b8',
                  borderRadius: 8,
                  padding: '4px 10px',
                  fontSize: 12,
                  cursor: 'pointer'
                }}
              >
                Annuler
              </button>
            )}
          </div>

          <form onSubmit={handleSubmit}>
            {/* Nom */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', color: '#94a3b8', fontSize: 13, fontWeight: 700, marginBottom: 6 }}>
                Nom & Prénom de l'Agent / Caissier *
              </label>
              <input
                type="text"
                className="ft-notes-input"
                placeholder="Ex: Mohamed Salah / Caissier Principal"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            {/* Localisation */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', color: '#94a3b8', fontSize: 13, fontWeight: 700, marginBottom: 6 }}>
                Localisation / Parc Assigné *
              </label>
              <select
                className="ft-notes-input"
                value={localisation}
                onChange={(e) => setLocalisation(e.target.value)}
                style={{ background: '#0f172a', color: '#f8fafc' }}
                required
              >
                <option value="Administration">🏛️ Administration Centrale</option>
                {locations.map((loc) => (
                  <option key={loc._id || loc.name} value={loc.name}>
                    🎢 {loc.name} {loc.category ? `(${loc.category})` : ''}
                  </option>
                ))}
              </select>
              <small style={{ color: '#64748b', fontSize: 11 }}>
                L'agent verra automatiquement les transferts destinés à cette localisation lorsqu'il se connecte.
              </small>
            </div>

            {/* Rôle */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', color: '#94a3b8', fontSize: 13, fontWeight: 700, marginBottom: 6 }}>
                Rôle de l'Agent *
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <button
                  type="button"
                  onClick={() => setRole('caissier')}
                  style={{
                    padding: '10px 12px',
                    borderRadius: 10,
                    border: role === 'caissier' ? '2px solid #38bdf8' : '1px solid rgba(255,255,255,0.1)',
                    background: role === 'caissier' ? 'rgba(56, 189, 248, 0.15)' : 'rgba(255,255,255,0.03)',
                    color: role === 'caissier' ? '#38bdf8' : '#94a3b8',
                    fontWeight: 700,
                    fontSize: 13,
                    cursor: 'pointer',
                    textAlign: 'center'
                  }}
                >
                  🎢 Caissier Récepteur
                </button>
                <button
                  type="button"
                  onClick={() => setRole('admin')}
                  style={{
                    padding: '10px 12px',
                    borderRadius: 10,
                    border: role === 'admin' ? '2px solid #ec4899' : '1px solid rgba(255,255,255,0.1)',
                    background: role === 'admin' ? 'rgba(236, 72, 153, 0.15)' : 'rgba(255,255,255,0.03)',
                    color: role === 'admin' ? '#ec4899' : '#94a3b8',
                    fontWeight: 700,
                    fontSize: 13,
                    cursor: 'pointer',
                    textAlign: 'center'
                  }}
                >
                  🏛️ Administrateur
                </button>
              </div>
            </div>

            {/* Code PIN (4 chiffres) */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', color: '#94a3b8', fontSize: 13, fontWeight: 700, marginBottom: 6 }}>
                Code PIN d'accès (4 Chiffres) *
              </label>
              
              {/* PIN Display Boxes */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: 12,
                padding: '14px',
                background: 'rgba(15, 23, 42, 0.8)',
                borderRadius: 12,
                border: '1px solid rgba(255, 255, 255, 0.1)',
                marginBottom: 12
              }}>
                {[0, 1, 2, 3].map((idx) => {
                  const digit = pinCode[idx];
                  return (
                    <div
                      key={idx}
                      style={{
                        width: 46,
                        height: 54,
                        borderRadius: 10,
                        border: digit ? '2px solid #38bdf8' : '2px dashed rgba(255, 255, 255, 0.2)',
                        background: digit ? 'rgba(56, 189, 248, 0.1)' : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 24,
                        fontWeight: 900,
                        color: '#f8fafc'
                      }}
                    >
                      {digit || '•'}
                    </div>
                  );
                })}
              </div>

              {/* Integrated Numeric Keypad for setting PIN */}
              <div className="ft-keypad-grid" style={{ maxWidth: 320, margin: '0 auto' }}>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                  <button
                    key={num}
                    type="button"
                    className="ft-key-btn"
                    onClick={() => handleKeypadPress(num.toString(), 'form')}
                  >
                    {num}
                  </button>
                ))}
                <button
                  type="button"
                  className="ft-key-btn ft-key-clear"
                  onClick={() => handleKeypadClear('form')}
                  title="Effacer tout"
                >
                  C
                </button>
                <button
                  type="button"
                  className="ft-key-btn"
                  onClick={() => handleKeypadPress('0', 'form')}
                >
                  0
                </button>
                <button
                  type="button"
                  className="ft-key-btn ft-key-del"
                  onClick={() => handleKeypadDelete('form')}
                  title="Supprimer un chiffre"
                >
                  ⌫
                </button>
              </div>
            </div>

            {/* Optional Phone & Notes */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
              <div>
                <label style={{ display: 'block', color: '#94a3b8', fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
                  Téléphone (Optionnel)
                </label>
                <input
                  type="text"
                  className="ft-notes-input"
                  placeholder="Ex: 98 123 456"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
              <div>
                <label style={{ display: 'block', color: '#94a3b8', fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
                  Notes / Observations
                </label>
                <input
                  type="text"
                  className="ft-notes-input"
                  placeholder="Ex: Équipe matin"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting || pinCode.length !== 4 || !name.trim()}
              className="ft-action-btn ft-btn-primary"
              style={{ width: '100%', padding: '14px', fontSize: 15 }}
            >
              {submitting ? 'Enregistrement en cours...' : isEditing ? '💾 Sauvegarder les Modifications' : '✅ Enregistrer le Nouvel Agent'}
            </button>
          </form>
        </div>

        {/* RIGHT COLUMN: Registered Agents List & PIN Tester */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          
          {/* Registered Agents Card */}
          <div className="ft-card" style={{ flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
              <h2 style={{ fontSize: 18, fontWeight: 800, color: '#f8fafc', margin: 0 }}>
                📋 Agents Enregistrés ({filteredAgents.length})
              </h2>
              
              <div style={{ display: 'flex', gap: 8 }}>
                {/* Search */}
                <input
                  type="text"
                  placeholder="🔍 Rechercher agent / parc..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    background: 'rgba(15,23,42,0.8)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: '#f8fafc',
                    borderRadius: 8,
                    padding: '6px 12px',
                    fontSize: 12
                  }}
                />

                {/* Location Filter */}
                <select
                  value={selectedLocFilter}
                  onChange={(e) => setSelectedLocFilter(e.target.value)}
                  style={{
                    background: 'rgba(15,23,42,0.8)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: '#f8fafc',
                    borderRadius: 8,
                    padding: '6px 10px',
                    fontSize: 12
                  }}
                >
                  <option value="ALL">Tous les Parcs</option>
                  <option value="Administration">Administration</option>
                  {locations.map(loc => (
                    <option key={loc._id || loc.name} value={loc.name}>{loc.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Chargement des agents...</div>
            ) : filteredAgents.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '30px 20px',
                background: 'rgba(15,23,42,0.4)',
                borderRadius: 12,
                border: '1px dashed rgba(255,255,255,0.1)'
              }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>👤</div>
                <div style={{ color: '#cbd5e1', fontWeight: 700 }}>Aucun agent trouvé</div>
                <div style={{ color: '#64748b', fontSize: 12, marginTop: 4 }}>
                  Utilisez le formulaire à gauche pour ajouter un agent et son code PIN.
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: '520px', overflowY: 'auto', paddingRight: 4 }}>
                {filteredAgents.map((agent) => {
                  const isPinVisible = showPinMap[agent._id];
                  return (
                    <div
                      key={agent._id}
                      style={{
                        padding: '12px 14px',
                        background: agent.isActive ? 'rgba(30, 41, 59, 0.7)' : 'rgba(15, 23, 42, 0.5)',
                        borderRadius: 12,
                        border: agent.isActive ? '1px solid rgba(255,255,255,0.08)' : '1px dashed rgba(239, 68, 68, 0.3)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 12,
                        opacity: agent.isActive ? 1 : 0.6
                      }}
                    >
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontWeight: 800, color: '#f8fafc', fontSize: 14 }}>
                            {agent.name}
                          </span>
                          <span style={{
                            fontSize: 10,
                            padding: '2px 8px',
                            borderRadius: 99,
                            fontWeight: 800,
                            background: agent.role === 'admin' ? 'rgba(236, 72, 153, 0.2)' : 'rgba(56, 189, 248, 0.2)',
                            color: agent.role === 'admin' ? '#ec4899' : '#38bdf8'
                          }}>
                            {agent.role === 'admin' ? '🏛️ Admin' : '🎢 Caissier'}
                          </span>
                          {!agent.isActive && (
                            <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 99, background: 'rgba(239, 68, 68, 0.2)', color: '#ef4444', fontWeight: 800 }}>
                              Inactif
                            </span>
                          )}
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4, fontSize: 12, color: '#94a3b8' }}>
                          <span>📍 <strong style={{ color: '#e2e8f0' }}>{agent.localisation}</strong></span>
                          {agent.phone && <span>📞 {agent.phone}</span>}
                        </div>

                        {/* PIN Code Box */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                          <span style={{ fontSize: 11, color: '#64748b' }}>Code PIN:</span>
                          <div style={{
                            background: 'rgba(0,0,0,0.4)',
                            padding: '2px 8px',
                            borderRadius: 6,
                            fontFamily: 'monospace',
                            fontWeight: 900,
                            letterSpacing: '2px',
                            color: isPinVisible ? '#38bdf8' : '#cbd5e1',
                            fontSize: 13
                          }}>
                            {isPinVisible ? agent.pinCode : '••••'}
                          </div>
                          <button
                            type="button"
                            onClick={() => toggleShowPin(agent._id)}
                            style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 12, color: '#94a3b8' }}
                            title={isPinVisible ? 'Masquer PIN' : 'Afficher PIN'}
                          >
                            {isPinVisible ? '👁️‍🗨️' : '👁️'}
                          </button>
                        </div>
                      </div>

                      {/* Actions */}
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <button
                          type="button"
                          onClick={() => openQrModal(agent)}
                          style={{
                            background: 'rgba(56, 189, 248, 0.1)',
                            border: '1px solid rgba(56, 189, 248, 0.3)',
                            color: '#38bdf8',
                            borderRadius: 8,
                            padding: '6px 10px',
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: 'pointer'
                          }}
                          title="Générer QR code d'accès mobile pour cet agent"
                        >
                          📱 QR Mobile
                        </button>
                        <button
                          type="button"
                          onClick={() => openPinModal(agent)}
                          style={{
                            background: 'rgba(245, 158, 11, 0.1)',
                            border: '1px solid rgba(245, 158, 11, 0.3)',
                            color: '#f59e0b',
                            borderRadius: 8,
                            padding: '6px 10px',
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: 'pointer'
                          }}
                          title="Changer rapidement le code PIN"
                        >
                          🔑 PIN
                        </button>
                        <button
                          type="button"
                          onClick={() => handleEditClick(agent)}
                          style={{
                            background: 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            color: '#e2e8f0',
                            borderRadius: 8,
                            padding: '6px 10px',
                            fontSize: 12,
                            cursor: 'pointer'
                          }}
                          title="Modifier les informations"
                        >
                          ✏️
                        </button>
                        <button
                          type="button"
                          onClick={() => handleToggleActive(agent)}
                          style={{
                            background: agent.isActive ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                            border: `1px solid ${agent.isActive ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                            color: agent.isActive ? '#10b981' : '#ef4444',
                            borderRadius: 8,
                            padding: '6px 10px',
                            fontSize: 12,
                            cursor: 'pointer'
                          }}
                          title={agent.isActive ? 'Désactiver' : 'Activer'}
                        >
                          {agent.isActive ? '✅' : '⛔'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(agent)}
                          style={{
                            background: 'rgba(239, 68, 68, 0.1)',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                            color: '#ef4444',
                            borderRadius: 8,
                            padding: '6px 10px',
                            fontSize: 12,
                            cursor: 'pointer'
                          }}
                          title="Supprimer définitivement"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Testeur de PIN en Direct */}
          <div className="ft-card" style={{ background: 'linear-gradient(135deg, rgba(15,23,42,0.9), rgba(30,41,59,0.9))' }}>
            <h3 style={{ fontSize: 15, fontWeight: 800, color: '#38bdf8', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>🧪 Testeur de Code PIN en Direct</span>
            </h3>
            <p style={{ color: '#94a3b8', fontSize: 12, marginBottom: 12 }}>
              Saisissez n'importe quel code à 4 chiffres pour vérifier instantanément l'identité et la localisation associées.
            </p>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{
                display: 'flex',
                gap: 8,
                padding: '8px 12px',
                background: '#0f172a',
                borderRadius: 10,
                border: '1px solid rgba(255,255,255,0.1)'
              }}>
                {[0, 1, 2, 3].map(i => (
                  <div key={i} style={{ width: 30, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 900, color: '#38bdf8' }}>
                    {testPin[i] || '•'}
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: 4 }}>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 0].map(n => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => handleKeypadPress(n.toString(), 'tester')}
                    style={{
                      width: 28,
                      height: 32,
                      borderRadius: 6,
                      background: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      color: '#f8fafc',
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                  >
                    {n}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => handleKeypadClear('tester')}
                  style={{
                    padding: '0 8px',
                    height: 32,
                    borderRadius: 6,
                    background: 'rgba(239,68,68,0.2)',
                    border: 'none',
                    color: '#ef4444',
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  Effacer
                </button>
              </div>
            </div>

            {testingPin && (
              <div style={{ marginTop: 10, color: '#38bdf8', fontSize: 12 }}>Vérification du code PIN...</div>
            )}

            {testResult && (
              <div style={{
                marginTop: 12,
                padding: '10px 14px',
                borderRadius: 8,
                background: testResult.success ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                border: `1px solid ${testResult.success ? '#10b981' : '#ef4444'}`,
                color: testResult.success ? '#10b981' : '#ef4444',
                fontSize: 12,
                fontWeight: 700
              }}>
                {testResult.msg}
              </div>
            )}
          </div>
        </div>

      </div>

      {/* MODAL: Modification Rapide du PIN */}
      {pinModalOpen && targetAgentForPin && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.85)',
          backdropFilter: 'blur(8px)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16
        }}>
          <div className="ft-card" style={{ maxWidth: 380, width: '100%', textAlign: 'center' }}>
            <h3 style={{ fontSize: 18, fontWeight: 800, color: '#f8fafc', marginBottom: 4 }}>
              🔑 Modifier le PIN de l'Agent
            </h3>
            <p style={{ color: '#94a3b8', fontSize: 13, marginBottom: 16 }}>
              {targetAgentForPin.name} ({targetAgentForPin.localisation})
            </p>

            {/* PIN Display */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 12,
              padding: '12px',
              background: 'rgba(15, 23, 42, 0.8)',
              borderRadius: 12,
              border: '1px solid rgba(255, 255, 255, 0.1)',
              marginBottom: 16
            }}>
              {[0, 1, 2, 3].map((idx) => {
                const digit = newPinInput[idx];
                return (
                  <div
                    key={idx}
                    style={{
                      width: 44,
                      height: 50,
                      borderRadius: 10,
                      border: digit ? '2px solid #f59e0b' : '2px dashed rgba(255, 255, 255, 0.2)',
                      background: digit ? 'rgba(245, 158, 11, 0.1)' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 22,
                      fontWeight: 900,
                      color: '#f8fafc'
                    }}
                  >
                    {digit || '•'}
                  </div>
                );
              })}
            </div>

            {/* Keypad */}
            <div className="ft-keypad-grid" style={{ maxWidth: 300, margin: '0 auto 16px' }}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                <button
                  key={num}
                  type="button"
                  className="ft-key-btn"
                  onClick={() => handleKeypadPress(num.toString(), 'modal')}
                >
                  {num}
                </button>
              ))}
              <button
                type="button"
                className="ft-key-btn ft-key-clear"
                onClick={() => handleKeypadClear('modal')}
              >
                C
              </button>
              <button
                type="button"
                className="ft-key-btn"
                onClick={() => handleKeypadPress('0', 'modal')}
              >
                0
              </button>
              <button
                type="button"
                className="ft-key-btn ft-key-del"
                onClick={() => handleKeypadDelete('modal')}
              >
                ⌫
              </button>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                type="button"
                className="ft-action-btn ft-btn-secondary"
                onClick={() => {
                  setPinModalOpen(false);
                  setTargetAgentForPin(null);
                  setNewPinInput('');
                }}
                style={{ flex: 1 }}
              >
                Annuler
              </button>
              <button
                type="button"
                className="ft-action-btn ft-btn-primary"
                disabled={newPinInput.length !== 4}
                onClick={handleSaveQuickPin}
                style={{ flex: 1 }}
              >
                Enregistrer PIN
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: QR Code Onboarding Mobile */}
      {qrModalOpen && selectedAgentForQr && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.85)',
          backdropFilter: 'blur(8px)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16
        }}>
          <div className="ft-card" style={{ maxWidth: 420, width: '100%', textAlign: 'center' }}>
            <h3 style={{ fontSize: 18, fontWeight: 800, color: '#f8fafc', marginBottom: 4 }}>
              📱 Accès Mobile pour {selectedAgentForQr.name}
            </h3>
            <p style={{ color: '#94a3b8', fontSize: 13, marginBottom: 16 }}>
              Localisation : <strong style={{ color: '#38bdf8' }}>{selectedAgentForQr.localisation}</strong>
            </p>

            {agentQrDataUrl && (
              <div style={{
                background: '#ffffff',
                padding: 16,
                borderRadius: 16,
                display: 'inline-block',
                boxShadow: '0 8px 30px rgba(0,0,0,0.4)',
                marginBottom: 16
              }}>
                <img src={agentQrDataUrl} alt="QR Accès Mobile" style={{ width: 220, height: 220, display: 'block' }} />
              </div>
            )}

            <div style={{
              background: 'rgba(15,23,42,0.8)',
              padding: '12px 16px',
              borderRadius: 12,
              border: '1px solid rgba(255,255,255,0.1)',
              marginBottom: 16,
              textAlign: 'left'
            }}>
              <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 4 }}>Instructions pour l'agent :</div>
              <div style={{ fontSize: 13, color: '#e2e8f0', lineHeight: 1.5 }}>
                1. Scannez ce QR code avec votre téléphone.<br />
                2. Saisissez votre code PIN : <strong style={{ color: '#38bdf8' }}>{selectedAgentForQr.pinCode}</strong><br />
                3. Vous accédez directement au portail de réception et remise de fonds de votre parc.
              </div>
            </div>

            <button
              type="button"
              className="ft-action-btn ft-btn-primary"
              onClick={() => {
                setQrModalOpen(false);
                setSelectedAgentForQr(null);
              }}
              style={{ width: '100%' }}
            >
              Fermer
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default GestionAgentsTransfert;
